import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  CalendarRange, ChevronLeft, ChevronRight, Loader2, Plus, MapPin, EyeOff, Clock,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { EventDialog } from "../components/employees/EventDialog";
import { PlanningNavigation } from "../components/employees/PlanningNavigation";
import { MobileEventButton } from "../components/employees/MobileEventButton";
import { getPlanningEvents, getEventTypes, getEmployees } from "../services/api";
import { typeIcon, STATUS_META, PRIORITY_META } from "../utils/eventVisuals";
import { useAuth } from "../context/AuthContext";

const iso = (d) => d.toLocaleDateString("fr-CA");
const mondayOf = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const ALL = "__all__";

const EventCard = ({ ev, date, assignees, onClick, clickable }) => {
  const Icon = typeIcon(ev.event_type);
  const st = STATUS_META[ev.status] || STATUS_META.todo;
  const prio = PRIORITY_META[ev.priority] || PRIORITY_META.normal;
  const cancelled = ev.status === "cancelled";
  return (
    <div
      className={`flex min-w-0 max-w-full items-start gap-3 bg-white border border-slate-200 rounded-xl p-3 ${clickable ? "cursor-pointer hover:border-[#26788A]/40 hover:shadow-sm" : ""} transition-[border-color,box-shadow] ${cancelled ? "opacity-50" : ""}`}
      style={{ borderLeft: `4px solid ${ev.color}` }}
      onClick={() => clickable && onClick(ev)}
      data-testid={`agenda-event-${ev.id}-${date}`}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={e => { if (clickable && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onClick(ev); } }}
    >
      <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: `${ev.color}22` }}>
        <Icon size={15} style={{ color: ev.color }} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className={`text-sm font-semibold text-slate-900 break-words [overflow-wrap:anywhere] ${cancelled ? "line-through" : ""}`}>{ev.title}</p>
          <span className="min-w-0 max-w-full break-words [overflow-wrap:anywhere] text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: ev.color }}>{ev.event_type}</span>
          {ev.visibility === "assigned" && (
            <span className="flex items-center gap-1 text-[10px] font-medium text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full" title="Visible uniquement par les assignés">
              <EyeOff size={10} /> Restreint
            </span>
          )}
        </div>
        <p className="text-xs text-slate-400 mt-0.5 flex flex-wrap items-center gap-x-2.5">
          {ev.start_time && <span className="flex items-center gap-1"><Clock size={10} /> {ev.start_time}{ev.end_time ? `–${ev.end_time}` : ""}</span>}
          {ev.location && <span className="min-w-0 max-w-full flex items-start gap-1"><MapPin size={10} className="shrink-0 mt-0.5" /><span className="min-w-0 break-words [overflow-wrap:anywhere]">{ev.location}</span></span>}
          <span className="flex items-center gap-1" style={{ color: st.color }}><st.icon size={10} /> {st.label}</span>
          {ev.priority && ev.priority !== "normal" && (
            <span className="flex items-center gap-1" style={{ color: prio.color }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: prio.color }}></span>{prio.label}
            </span>
          )}
        </p>
        {assignees.length > 0 && <p className="sm:hidden mt-1 text-xs text-slate-500 break-words [overflow-wrap:anywhere]" data-testid={`agenda-assignees-${ev.id}-${date}`}>{assignees.map(e => `${e.first_name} ${e.last_name}`).join(", ")}</p>}
      </div>
      <div className="hidden sm:flex -space-x-1.5 shrink-0">
        {assignees.slice(0, 3).map((e) => (
          <span key={e.id} className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center text-[8px] font-bold text-white"
            style={{ backgroundColor: e.color || "#26788A" }} title={`${e.first_name} ${e.last_name}`}>
            {(e.first_name?.[0] || "") + (e.last_name?.[0] || "")}
          </span>
        ))}
        {assignees.length > 3 && <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-600 text-[9px] flex items-center justify-center" data-testid={`agenda-more-assignees-${ev.id}-${date}`}>+{assignees.length - 3}</span>}
      </div>
    </div>
  );
};

export default function EventsPage() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || !!user?.can_manage_events;
  const [refDate, setRefDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [types, setTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [familyFilter, setFamilyFilter] = useState(ALL);
  const [viewMode, setViewMode] = useState("week");
  const [dialog, setDialog] = useState(null); // {event?} | {defaultDate}

  const monday = mondayOf(refDate);
  const monthStart = new Date(refDate.getFullYear(), refDate.getMonth(), 1);
  const gridStart = mondayOf(monthStart);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const m = mondayOf(refDate);
      const start = viewMode === "month" ? mondayOf(new Date(refDate.getFullYear(), refDate.getMonth(), 1)) : m;
      const end = viewMode === "month" ? addDays(start, 41) : addDays(m, 6);
      const res = await getPlanningEvents(iso(start), iso(end));
      setEvents(res.data);
    } catch {
      toast.error("Erreur de chargement des événements");
    } finally {
      setLoading(false);
    }
  }, [refDate, viewMode]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getEventTypes().then((r) => setTypes(r.data)).catch(() => {});
    getEmployees().then((r) => setEmployees(r.data)).catch(() => {});
  }, []);

  const days = Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  const filtered = events.filter((ev) => familyFilter === ALL || ev.event_type === familyFilter);
  const eventsFor = (dIso) => filtered
    .filter((ev) => ev.date <= dIso && dIso <= (ev.end_date || ev.date))
    .sort((a, b) => (a.start_time || "99") < (b.start_time || "99") ? -1 : 1);
  const assigneesOf = (ev) => employees.filter((e) => (ev.employee_ids || []).includes(e.id));

  return (
    <div className="planning-page p-4 lg:p-8 max-w-5xl mx-auto min-w-0 pb-24" data-testid="events-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <CalendarRange size={24} className="text-[#26788A]" /> Événements
        </h1>
        {canManage && (
          <Button size="sm" className="hidden lg:flex bg-[#26788A] hover:bg-[#1D5E6C] text-white" onClick={() => setDialog({ defaultDate: iso(new Date()) })} data-testid="add-agenda-event-btn">
            <Plus size={14} className="mr-1" /> Nouvel événement
          </Button>
        )}
      </div>

      <PlanningNavigation selectedDate={refDate} onDateChange={setRefDate} title={viewMode === "month" ? refDate.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }) : `Du ${monday.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })} au ${addDays(monday, 6).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })}`} onPrevious={() => setRefDate(viewMode === "month" ? new Date(refDate.getFullYear(), refDate.getMonth() - 1, 1) : addDays(refDate, -7))} onNext={() => setRefDate(viewMode === "month" ? new Date(refDate.getFullYear(), refDate.getMonth() + 1, 1) : addDays(refDate, 7))} onToday={() => setRefDate(new Date())} previousId="agenda-prev" nextId="agenda-next" titleId="agenda-period" todayId="agenda-today" />
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="flex bg-slate-100 rounded-lg p-0.5">
          <button className={`px-3 py-1.5 rounded-md text-xs font-medium ${viewMode === "week" ? "bg-white shadow-sm text-[#26788A]" : "text-slate-500"}`} onClick={() => setViewMode("week")} data-testid="agenda-view-week">Semaine</button>
          <button className={`px-3 py-1.5 rounded-md text-xs font-medium ${viewMode === "month" ? "bg-white shadow-sm text-[#26788A]" : "text-slate-500"}`} onClick={() => setViewMode("month")} data-testid="agenda-view-month">Mois</button>
        </div>
        <Select value={familyFilter} onValueChange={setFamilyFilter}>
          <SelectTrigger className="w-48 h-10 text-xs" data-testid="family-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Toutes les familles</SelectItem>
            {types.map((t) => (
              <SelectItem key={t.id} value={t.name}>
                <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: t.color }}></span>{t.name}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={26} className="animate-spin text-slate-300" /></div>
      ) : viewMode === "month" ? (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="agenda-month-grid">
          <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50/60">
            {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((d) => (
              <div key={d} className="text-[10px] font-bold text-slate-500 uppercase text-center py-2">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {Array.from({ length: 42 }, (_, i) => {
              const d = addDays(gridStart, i);
              const dIso = iso(d);
              const inMonth = d.getMonth() === refDate.getMonth();
              const isToday = dIso === iso(new Date());
              const dayEvents = eventsFor(dIso);
              return (
                <div key={dIso}
                  className={`min-w-0 min-h-[92px] border-b border-r border-slate-100 p-1 sm:p-1.5 ${inMonth ? "" : "bg-slate-50/60"} ${canManage ? "cursor-pointer hover:bg-teal-50/40" : ""}`}
                  onClick={() => canManage && setDialog({ defaultDate: dIso })}
                  data-testid={`month-cell-${dIso}`}>
                  <span className={`inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold rounded-full ${isToday ? "bg-[#26788A] text-white" : inMonth ? "text-slate-700" : "text-slate-300"} ${d.getDay() === 0 && inMonth ? "text-red-500" : ""}`}>
                    {d.getDate()}
                  </span>
                  <div className="min-w-0 space-y-0.5 mt-0.5">
                    {dayEvents.slice(0, 3).map((ev) => (
                      <button key={ev.id}
                        className={`w-full text-left text-[9px] font-semibold text-white px-1.5 py-0.5 rounded truncate block ${ev.status === "cancelled" ? "opacity-40 line-through" : ""}`}
                        style={{ backgroundColor: ev.color }}
                        title={`${ev.title} (${ev.event_type})`}
                        onClick={(e) => { e.stopPropagation(); setDialog({ event: ev }); }}
                        data-testid={`month-event-${ev.id}-${dIso}`}>
                        {ev.start_time ? `${ev.start_time} ` : ""}{ev.title}
                      </button>
                    ))}
                    {dayEvents.length > 3 && <p className="text-[9px] text-slate-400 pl-1">+{dayEvents.length - 3} autre(s)</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {days.map((d) => {
            const dIso = iso(d);
            const dayEvents = eventsFor(dIso);
            const isToday = dIso === iso(new Date());
            const isSunday = d.getDay() === 0;
            return (
              <div key={dIso} className="flex flex-col sm:flex-row gap-2 sm:gap-4" data-testid={`agenda-day-${dIso}`}>
                <div className={`sm:w-[110px] shrink-0 flex sm:block items-center gap-2 sm:text-right pt-2 ${isSunday ? "text-red-500" : ""}`}>
                  <p className={`text-sm font-bold capitalize ${isToday ? "text-[#26788A]" : isSunday ? "text-red-500" : "text-slate-800"}`}>
                    {d.toLocaleDateString("fr-FR", { weekday: "long" })}
                  </p>
                  <p className="text-xs text-slate-400">{d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })}</p>
                  {isToday && <span className="inline-block mt-1 text-[9px] font-bold text-white bg-[#26788A] px-2 py-0.5 rounded-full">Aujourd'hui</span>}
                </div>
                <div className={`flex-1 min-w-0 space-y-2 border-l-2 pl-3 sm:pl-4 pb-2 ${isToday ? "border-[#26788A]/50" : "border-slate-100"}`}>
                  {dayEvents.length === 0 ? (
                    <button
                      className={`w-full text-left text-xs text-slate-300 py-3 px-3 rounded-lg border border-dashed border-slate-200 ${canManage ? "hover:border-[#26788A]/40 hover:text-slate-400" : "cursor-default"}`}
                      onClick={() => canManage && setDialog({ defaultDate: dIso })}
                      data-testid={`agenda-empty-${dIso}`}>
                      {canManage ? "+ Ajouter un événement" : "Aucun événement"}
                    </button>
                  ) : dayEvents.map((ev) => (
                    <EventCard key={ev.id} ev={ev} date={dIso} assignees={assigneesOf(ev)} clickable onClick={(e) => setDialog({ event: e })} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {canManage && <MobileEventButton onClick={() => setDialog({ defaultDate: new Date().toLocaleDateString("fr-CA") })} />}
      <EventDialog
        open={!!dialog}
        onClose={() => setDialog(null)}
        event={dialog?.event}
        defaultDate={dialog?.defaultDate}
        onSaved={load}
      />
    </div>
  );
}
